package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.Company
import com.solevo.dist.application.models.Customer
import com.solevo.dist.application.models.User
import com.solevo.dist.repository.ApplicationCustomerRepository
import com.solevo.dist.network.api.user.UserRemoteSource
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.launch
import org.koin.mp.KoinPlatform.getKoin

enum class SettingsTab { Personal, Company }

data class SettingsItemUi(
    val title: String,
    val value: String,
    val valueColorHex: String? = null,
    val isLanguage: Boolean = false,
    val isTermsAndPrivacy: Boolean = false
)

data class SettingsUiState(
    val selectedTab: SettingsTab = SettingsTab.Personal,
    val personal: List<SettingsItemUi> = emptyList(),
    val company: List<SettingsItemUi> = emptyList(),
    val languages: List<Pair<String, String>> = listOf("English" to "en", "Français" to "fr", "Português" to "pt"), // label -> code
    val selectedLanguageCode: String = "en",
    val showLanguageCard: Boolean = false
)

class SettingsViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val userRemoteSource: UserRemoteSource = getKoin().get()
    private val customerRepository: ApplicationCustomerRepository = getKoin().get()
    // Localization hooked at viewmodel-level for Apply button
    // Keeps UI reactive via CompositionLocal provider
    private val localization = com.solevo.dist.i18n.Localization

    private val _state = MutableStateFlow(
        SettingsUiState(
            selectedLanguageCode = localization.language.value
        )
    )
    val state: StateFlow<SettingsUiState> = _state.asStateFlow()

    @OptIn(ExperimentalCoroutinesApi::class)
    fun start() {
        scope.launch {
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest { user ->
                    if (user.customerId.isNotEmpty()) {
                        kotlinx.coroutines.flow.flow { emit(user) }
                            .combine(customerRepository.getCurrentCustomer()) { u, customer ->
                                Pair(u, customer)
                            }
                    } else {
                        kotlinx.coroutines.flow.flow<Pair<com.solevo.dist.application.models.User, Customer?>> { }
                    }
                }
                .collectLatest { (user, customer) ->
                    val personalItems = buildList {
                        val fullName = listOfNotNull(user.firstName, user.lastName)
                            .filter { it.isNotBlank() }
                            .joinToString(" ")
                            .ifBlank { "—" }
                        add(SettingsItemUi(title = "Name", value = fullName))
                        add(SettingsItemUi(title = "Customer ID", value = user.customerId))
                        add(SettingsItemUi(title = "Country", value = customer?.company?.country.orEmpty()))
                        val currentLang = localization.language.value
                        val currentLangLabel = languageLabel(currentLang)
                        add(SettingsItemUi(title = "Language", value = currentLangLabel, isLanguage = true))
                        add(SettingsItemUi(title = "Phone number", value = user.phoneNumber))
                        add(
                            SettingsItemUi(
                                title = "Terms & Privacy",
                                value = "",
                                isTermsAndPrivacy = true
                            )
                        )
                    }

                    val companyItems = buildList {
                        add(SettingsItemUi(title = "Name", value = customer?.company?.name.orEmpty()))
                        add(SettingsItemUi(title = "ID", value = customer?.customerId.orEmpty()))
                        add(SettingsItemUi(title = "Address", value = customer?.company?.address.orEmpty()))
                        add(SettingsItemUi(title = "Tier", value = customer?.company?.tier.toTierDisplay()))
                    }

                    val selectedLang = localization.language.value

                    _state.value = _state.value.copy(
                        personal = personalItems,
                        company = companyItems,
                        selectedLanguageCode = selectedLang
                    )
                }
        }
    }

    private fun normalizeLanguageCode(code: String?): String {
        val current = localization.language.value
        if (code.isNullOrBlank()) return current
        val lc = code.lowercase()
        return when {
            lc.startsWith("en") -> "en"
            lc.startsWith("fr") -> "fr"
            lc.startsWith("pt") -> "pt"
            else -> current
        }
    }

    fun stop() = scope.cancel()

    fun selectTab(tab: SettingsTab) {
        _state.value = _state.value.copy(selectedTab = tab)
    }

    fun openLanguageCard() {
        _state.value = _state.value.copy(showLanguageCard = true)
    }

    fun dismissLanguageCard() {
        _state.value = _state.value.copy(showLanguageCard = false)
    }

    fun setSelectedLanguage(code: String) {
        _state.value = _state.value.copy(selectedLanguageCode = code)
    }

    fun applySelectedLanguage() {
        localization.setLanguage(_state.value.selectedLanguageCode)
        val updatedPersonal = _state.value.personal.map {
            if (it.isLanguage) it.copy(value = languageLabel(_state.value.selectedLanguageCode)) else it
        }
        _state.value = _state.value.copy(showLanguageCard = false, personal = updatedPersonal)
    }

    fun logout() {
        scope.launch {
            runCatching {
                Firebase.auth.signOut()
            }
        }
    }

    private fun languageLabel(code: String): String {
        val mapping = _state.value.languages
        return mapping.firstOrNull { it.second.equals(code, ignoreCase = true) }?.first ?: code
    }
}

private fun String?.toTierDisplay(): String {
    return if (this.equals("Premium", ignoreCase = true)) "Club Partner" else "Distributor"
}

